import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import Clickable from 'components/Common/Clickable'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import withMountGuard from 'hocs/withMountGuard'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { nonNullable } from 'lib/array/arrayUtils'
import { useIntl } from 'react-intl'

const Container = styled(Clickable)`
  padding: ${rem(12)} ${rem(16)};
  color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
  border-radius: ${props => props.theme.borderRadius.M};
  /** To see the effect of the border-radius */
  overflow: hidden;
`

const CustomDivider = styled(Divider)`
  && {
    background-color: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
  }
`

interface Props {
  onLinkClick: () => void
  config: App.LoyaltyConfig
}

function AccountMenuLoyaltyNoAccountGoldTile({ onLinkClick, config }: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const handleActivateMembership = useCallback(() => {
    dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    onLinkClick()
  }, [dispatch, onLinkClick])

  const { regionTierBenefits } = config

  const goldLabel = intl.formatMessage({
    id: 'L8EUxyRI1m9QV-ilkf3k',
    defaultMessage: 'Gold',
  })

  const exclusiveInviteLabel = intl.formatMessage({
    id: 'DgndYcWV8ZuJwvmsNktn',
    defaultMessage: 'EXCLUSIVE INVITE',
  })

  const unlockedMessage = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'b-bJL9pdwvmG7fsGakD6',
        defaultMessage: 'You’ve unlocked <goldStatus>Gold status</goldStatus>',
      },
      {
        goldStatus: (chunks: React.ReactNode) => (
          <Colour key="gold-status" value="tier-gold-light-contrast">
            {chunks}
          </Colour>
        ),
      },
    )
  }, [intl])

  const benefitsReadyMessage = intl.formatMessage({
    id: 'XcbZtx424rtoCLg3K1mZ',
    defaultMessage: 'Your benefits, ready when you are',
  })

  const freeRoomUpgradeLabel = intl.formatMessage({
    id: 'RSqha5mkje2juEWfrslW',
    defaultMessage: 'Free room or package upgrade',
  })

  const freeAirportChauffeurLabel = intl.formatMessage({
    id: 'MDNivoqeXgYwSbjks_MC',
    defaultMessage: 'Free airport chauffeur service',
  })

  const freeAirportLoungeLabel = intl.formatMessage({
    id: '4js5sbGZFSJwD0MGH8n_',
    defaultMessage: 'Free airport lounge passes',
  })

  const discountedInsuranceLabel = intl.formatMessage({
    id: '_MMI84-00swiulgIraoz',
    defaultMessage: 'Discounted travel insurance',
  })

  const priorityPhoneSupportLabel = intl.formatMessage({
    id: 'lvEXAwC6QxaeMJuzxbkn',
    defaultMessage: 'Priority phone support 24/7',
  })

  const activateButtonLabel = intl.formatMessage({
    id: '6s7T1mheugtdtW37DNm0',
    defaultMessage: 'Activate free membership',
  })

  const unlimitedLabel = intl.formatMessage({
    id: 'bx_oL2NLaGpopuYDxhtr',
    defaultMessage: 'Unlimited',
  })

  const formatBenefitLimit = useCallback((benefit: App.LoyaltyRegionTierBenefit): string => {
    if (benefit.unlimited) {
      return unlimitedLabel
    }
    return intl.formatMessage(
      {
        id: '0ziaQF9Smqpyhw5pYHEB',
        defaultMessage: '{count}x',
      },
      { count: benefit.limit },
    )
  }, [intl, unlimitedLabel])

  const formatBenefitPercentage = useCallback((amount: number): string => {
    return intl.formatMessage(
      {
        id: 'ZbLn7gDcA9olWN7pTHQm',
        defaultMessage: '{amount}%',
      },
      { amount },
    )
  }, [intl])

  const benefits = useMemo((): Array<App.LoyaltyRegionTierBenefit> => {
    const orderedBenefits: Array<App.LoyaltyBenefitType> = [
      'hotel_upgrade',
      'airport_transfer',
      'lounge_pass',
      'discounted_insurance',
      'phone_support',
    ]

    return nonNullable(orderedBenefits.map(benefitType => {
      const benefit = regionTierBenefits.gold.find(benefit => benefit.code === benefitType)
      if (benefit?.code === 'phone_support') {
        const loungePassBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'lounge_pass')
        // We don't need to show phone support if we have a lounge pass available
        if (loungePassBenefit) return
      }
      return benefit
    }))
  }, [regionTierBenefits])

  return <Container to={`/${LOYALTY_LANDING_PAGE}`} onClick={onLinkClick}>
    <VerticalSpacer gap={20}>
      <VerticalSpacer gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <LoyaltyProgramNameLogo width={72} />
          <BodyText variant="medium" format="uppercase">{goldLabel}</BodyText>
        </Group>
        <LoyaltyTierLabel
          tier="gold"
          kind="primary"
          format="uppercase"
        >
          {exclusiveInviteLabel}
        </LoyaltyTierLabel>
        <Heading variant="heading4">
          {unlockedMessage}
        </Heading>
      </VerticalSpacer>
      <VerticalSpacer gap={8}>
        <BodyText variant="small" weight="bold">
          {benefitsReadyMessage}
        </BodyText>
        {benefits.map((benefit, idx) => (
          <React.Fragment key={benefit.code}>
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
              {benefit.code === 'hotel_upgrade' && <>
                <BodyTextBlock variant="small" startIcon={<LineCrownIcon />}>
                  {freeRoomUpgradeLabel}
                </BodyTextBlock>
                <Group direction="horizontal" verticalAlign="center" gap={12}>
                  <LoyaltyTierLabel kind="tertiary" tier="gold">
                    {formatBenefitLimit(benefit)}
                  </LoyaltyTierLabel>
                </Group>
              </>}
              {benefit.code === 'airport_transfer' && <>
                <BodyTextBlock variant="small" startIcon={<LineTaxiIcon />}>
                  {freeAirportChauffeurLabel}
                </BodyTextBlock>
                <LoyaltyTierLabel kind="tertiary" tier="gold">
                  {formatBenefitLimit(benefit)}
                </LoyaltyTierLabel>
              </>}
              {benefit.code === 'lounge_pass' && <>
                <BodyTextBlock variant="small" startIcon={<LineChairWithAirplaneIcon />}>
                  {freeAirportLoungeLabel}
                </BodyTextBlock>
                <LoyaltyTierLabel kind="tertiary" tier="gold">
                  {formatBenefitLimit(benefit)}
                </LoyaltyTierLabel>
              </>}
              {benefit.code === 'discounted_insurance' && <>
                <BodyTextBlock variant="small" startIcon={<LineShieldCheckIcon />}>
                  {discountedInsuranceLabel}
                </BodyTextBlock>
                <LoyaltyTierLabel kind="tertiary" tier="gold">
                  {benefit.value?.amount ? formatBenefitPercentage(benefit.value.amount) : ''}
                </LoyaltyTierLabel>
              </>}
              {benefit.code === 'phone_support' && <BodyTextBlock variant="small" startIcon={<LinePhoneIcon />}>
                {priorityPhoneSupportLabel}
              </BodyTextBlock>}
            </Group>
            {idx < benefits.length - 1 && <CustomDivider kind="primary" />}
          </React.Fragment>
        ))}
      </VerticalSpacer>
      <TextButton kind="primary" size="medium" fit="full-width" onClick={handleActivateMembership}>
        {activateButtonLabel}
      </TextButton>
    </VerticalSpacer>
  </Container>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyNoAccountGoldTile)
